import { EllipsedText, FlexColumn, FlexRow, Loading, MaterialIcon } from '@gorila-shared-ui/components';
import { NotificationCircle } from 'baseui/badge';
import { Block } from 'baseui/block';
import { Button } from 'baseui/button';
import { Card } from 'baseui/card';
import { LabelSmall } from 'baseui/typography';
import { CSSProperties, memo, useState } from 'react';
import { generatePath } from 'react-router-dom';
import { ListChildComponentProps } from 'react-window';
import { useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil';
import { NOTIFICATION_PAGE } from '../../constants/app';
import { useFeedback } from '../../hooks/useFeedback';
import { useStyles } from '../../hooks/useStyles';
import { markNotification } from '../../services/notificationService';
import { apiParamsState } from '../../storage/app';
import { notificationState, notificationsState } from '../../storage/notifications';
import { NotificationCore, NotificationLinkableSection } from '../../types/notification';
import MediaThumbnail from '../shared/MediaThumbnail';
import { StyledTooltip } from '../ui/StyledTooltip';
import NotificationBody from './NotificationBody';
import { useNotificationAction } from './hooks/useNotificationAction';

const EvidencesPreview = memo(({ notification }: { notification: NotificationCore }) => {
  const { theme, css } = useStyles();
  if (
    !notification?.origin ||
    notification.actions?.viewDetails.section !== NotificationLinkableSection.alarmEvidences
  ) {
    return null;
  }
  const evidencesCount = notification.actions.viewDetails.params.evidences.length;
  const mediaKey = notification.actions.viewDetails.params.evidences.at(0)?.key;
  return mediaKey ? (
    <NotificationCircle
      content={evidencesCount}
      color="accent"
      placement="topRight"
      horizontalOffset="2px"
      verticalOffset="-2px"
      hidden={evidencesCount <= 1}
      overrides={{
        Badge: {
          style: () => ({
            fontSize: '9px',
            width: '16px',
            height: '16px',
            lineHeight: '13px',
            borderRadius: '6px',
            paddingLeft: '2px',
          }),
        },
      }}
    >
      <Block
        className={css({
          width: '72px',
          borderRadius: theme.borders.popoverBorderRadius,
          overflow: 'hidden',
        })}
      >
        <MediaThumbnail
          mediaKey={mediaKey}
          origin={notification.origin}
          minHeight={72}
          onlyPreview
          cover
        />
      </Block>
    </NotificationCircle>
  ) : null;
});

function NotificationItem({
  data,
  index,
  style,
}: Readonly<
  ListChildComponentProps<{
    items: string[];
    isLoaded: (index: number) => boolean;
    onClose: () => void;
  }>
>) {
  const { css, theme } = useStyles();
  const { showFailFeedback } = useFeedback();
  const notificationId = data.items[index];
  const [notification, setNotification] = useRecoilState(notificationState(notificationId));
  const [loading, setLoading] = useState(false);
  const apiParams = useRecoilValue(apiParamsState);
  const setNotifications = useSetRecoilState(notificationsState);
  const { routeAction } = useNotificationAction(notification, data.onClose);

  const isRead = notification?.read;

  const readNotification = async () => {
    if (!apiParams) return;
    setNotification((prev) => {
      return { ...prev!, read: true };
    });
    const { error } = await markNotification(apiParams.clientId, apiParams.subClientId, notificationId, 'read');
    if (error) {
      showFailFeedback(error || 'Ocurrió un error al marcar como leída la notificación, inténtalo más tarde.');
      setNotification((prev) => {
        return { ...prev!, read: false };
      });
    }
  };

  const deleteNotification = async () => {
    setLoading(true);
    if (!apiParams) return;
    const { error } = await markNotification(apiParams.clientId, apiParams.subClientId, notificationId, 'discard');
    if (!error) {
      setNotifications((prev) => {
        return prev?.filter((n) => n._id !== notificationId);
      });
    } else {
      showFailFeedback(error || 'Ocurrió un error al eliminar la notificación, inténtalo más tarde.');
    }
    setLoading(false);
  };

  const centerLoader = !data.isLoaded(index)
    ? {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
      }
    : {};

  return (
    <span style={style as CSSProperties}>
      <Card
        overrides={{
          Root: {
            style: {
              height: 'calc(100% - 8px)',
              boxSizing: 'border-box',
              borderLeftWidth: '1px',
              borderRightWidth: '1px',
              borderTopWidth: '1px',
              borderBottomWidth: '1px',
              backgroundColor: !isRead ? theme.colors.backgroundSecondary : undefined,
              userSelect: 'none',
              ...centerLoader,
            },
          },
          Contents: {
            style: {
              marginTop: 0,
              marginRight: 0,
              marginBottom: 0,
              marginLeft: 0,
              padding: `${theme.sizing.scale300} ${theme.sizing.scale500}`,
            },
          },
          Body: {
            style: {
              marginBottom: 0,
            },
          },
        }}
      >
        {data.isLoaded(index) && notification && (
          <FlexRow gap={theme.sizing.scale400}>
            <EvidencesPreview notification={notification} />
            <FlexColumn
              gap={theme.sizing.scale100}
              classNames={css({ width: '100%' })}
            >
              <FlexRow
                classNames={css({
                  alignItems: 'center',
                  justifyContent: 'space-between',
                })}
              >
                <LabelSmall>
                  <EllipsedText inline>{notification.title || '-'}</EllipsedText>
                </LabelSmall>
                <FlexRow gap={theme.sizing.scale100}>
                  {Object.values(notification.actions || {}).map((action, index) => (
                    <StyledTooltip
                      key={`notification-action-${index}`}
                      content={action.title}
                      showArrow={false}
                      placement="bottom"
                      ignoreBoundary
                    >
                      <Button
                        shape="circle"
                        kind="tertiary"
                        size="mini"
                        onClick={() => {
                          routeAction(action);
                        }}
                      >
                        <MaterialIcon
                          name={action.icon}
                          size="18px"
                        />
                      </Button>
                    </StyledTooltip>
                  ))}
                  <StyledTooltip
                    content="Abrir notificación"
                    showArrow={false}
                    placement="bottom"
                    ignoreBoundary
                  >
                    <Button
                      shape="circle"
                      kind="tertiary"
                      size="mini"
                      onClick={() =>
                        apiParams &&
                        window.open(
                          generatePath(NOTIFICATION_PAGE.route, {
                            id: apiParams.subClientId,
                            notificationId: notificationId,
                          }),
                          '_blank',
                          'directories=no,titlebar=no,toolbar=no,location=no,status=no,menubar=no,scrollbars=no,resizable=no, height=700px, width=1400px',
                        )
                      }
                    >
                      <MaterialIcon
                        name="open_in_new"
                        size="18px"
                      />
                    </Button>
                  </StyledTooltip>
                  {!notification.read && (
                    <StyledTooltip
                      content="Marcar como leida"
                      showArrow={false}
                    >
                      <Button
                        size="mini"
                        shape="circle"
                        onClick={readNotification}
                        kind="tertiary"
                      >
                        <MaterialIcon
                          name="task_alt"
                          size="18px"
                        />
                      </Button>
                    </StyledTooltip>
                  )}
                  <StyledTooltip
                    content="Borrar"
                    showArrow={false}
                  >
                    <Button
                      size="mini"
                      shape="circle"
                      onClick={deleteNotification}
                      kind="tertiary"
                      isLoading={loading}
                    >
                      <MaterialIcon
                        name="delete"
                        size="18px"
                      />
                    </Button>
                  </StyledTooltip>
                </FlexRow>
              </FlexRow>
              <NotificationBody notification={notification} />
            </FlexColumn>
          </FlexRow>
        )}
        {!data.isLoaded(index) && <Loading size="compact" />}
      </Card>
    </span>
  );
}

export default memo(NotificationItem);
